// SETTINGS V2 — MAP-A-FE2 overview map section (§5). The Lokácie list drawn
// geographically: ONE pin per LOCATED node (parent + child) derived from the
// SAME visibleLocations array the list renders, so hidden/greyed state and the
// pin-click → scroll/flash contract stay structurally consistent (§5.5/§5.6).
//
// This is the OUTER half (section frame + key/empty/fold guards). It reads the
// Maps key and renders a graceful placeholder WITHOUT ever calling useJsApiLoader
// (hooks can't be conditional — the loader lives in LocationsMapCanvasV2, mounted
// only when a key exists AND ≥1 pin is located AND the section is expanded).
//
// v2 stack: inline styles + scoped CSS-var tokens; useBreakpoint (never MUI
// useMediaQuery). NO MUI/dt/Tailwind authored. SEC-REQ-2/3: no import-time side
// effects, no hardcoded ids/keys (key read at render).
import React, { useEffect, useMemo, useState } from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import type { Location } from "../../../machinery/components/locations/locationTypes";
import LocationsMapCanvasV2, {
  MapPlaceholder,
  getGoogleMapsApiKey,
} from "./LocationsMapCanvasV2";

const flattenNodes = (locations: Location[]): Location[] => {
  const out: Location[] = [];
  for (const p of locations) {
    out.push(p);
    for (const c of p.childLocations ?? []) out.push(c);
  }
  return out;
};

const isLocated = (l: Location): boolean =>
  l.latitude != null && l.longitude != null;

export interface LocationsMapV2Props {
  /** The page's visibleLocations — ALREADY inactive-filtered (§5.5). */
  locations: Location[];
  onPinClick: (loc: Location) => void;
  /** Currently selected/focused location id → its pin renders red (§enhancement). */
  selectedId?: string | null;
  /** Bumped by the page on each ROW-pin click → expand (if collapsed) + pan/zoom
   *  the map to `selectedId`'s pin. A monotonically increasing nonce so re-focusing
   *  the same id (or re-focusing after a background deselect) always re-triggers. */
  focusNonce?: number;
  /** Click on the map background (not a pin) → deselect. */
  onBackgroundClick?: () => void;
}

const LocationsMapV2: React.FC<LocationsMapV2Props> = ({
  locations,
  onPinClick,
  selectedId,
  focusNonce,
  onBackgroundClick,
}) => {
  const { translate: t } = useTranslation("settings");
  const { isPhone } = useBreakpoint();
  // Default: expanded on desktop/tablet, collapsed on phone (list-first). No
  // persistence — mirrors the page's showInactive pattern; initialised once.
  const [expanded, setExpanded] = useState(!isPhone);

  // A row-pin click (focusNonce bump) auto-expands the map so the focus is visible.
  useEffect(() => {
    if (focusNonce) setExpanded(true);
  }, [focusNonce]);

  const allNodes = useMemo(() => flattenNodes(locations), [locations]);
  const pins = useMemo(() => allNodes.filter(isLocated), [allNodes]);
  const located = pins.length;
  const total = allNodes.length;
  const unlocatedCount = total - located;

  // No locations at all (or all hidden) → the page's own empty state covers it;
  // hide the section entirely (§5.4).
  if (total === 0) return null;

  const hasKey = !!getGoogleMapsApiKey();
  const mapHeight = isPhone ? 220 : 320;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Header line — eyebrow title + mono counter + fold button. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span className="eyebrow" style={{ color: "var(--muted)" }}>
          {t("settings:locations.map.title")}
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--faint)",
            letterSpacing: "0.04em",
            flex: 1,
            minWidth: 0,
          }}
        >
          {t("settings:locations.map.counter", { located, total })}
        </span>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          style={{ flexShrink: 0 }}
        >
          {expanded ? t("settings:locations.map.hide") : t("settings:locations.map.show")}
        </button>
      </div>

      {/* Body — only mounted when expanded (lazy Maps-script load; §5.4). */}
      {expanded &&
        (!hasKey ? (
          <MapPlaceholder height={mapHeight} text={t("settings:locations.map.loadError")} />
        ) : located === 0 ? (
          <MapPlaceholder
            height={mapHeight}
            title={t("settings:locations.map.emptyTitle")}
            text={t("settings:locations.map.emptyHint")}
          />
        ) : (
          <>
            <LocationsMapCanvasV2
              pins={pins}
              onPinClick={onPinClick}
              height={mapHeight}
              selectedId={selectedId}
              focusNonce={focusNonce}
              onBackgroundClick={onBackgroundClick}
            />
            {/* Unlocated notice — ≥1 pin AND ≥1 visible node without coordinates. */}
            {unlocatedCount > 0 && (
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--faint)",
                  letterSpacing: "0.04em",
                }}
              >
                {t("settings:locations.map.noCoords", { count: unlocatedCount })}
              </div>
            )}
          </>
        ))}
    </section>
  );
};

export default LocationsMapV2;
